<template>
  <div class="pl-1">
    <v-dialog v-model="menu" max-width="700" persistent content-class="cluster-select-dialog">
      <template v-slot:activator="{ on, attrs }">
        <v-btn v-if="large" small v-bind="attrs" v-on="on" color="primary" :disabled="disabled" @click="loadClusters()">
          <v-icon left small>mdi-hexagon-multiple</v-icon>
          Add to Cluster
        </v-btn>
        <v-btn v-else icon v-bind="attrs" v-on="on" large color="secondary" :disabled="disabled"
          @click="loadClusters()">
          <v-icon>mdi-hexagon-multiple</v-icon>
        </v-btn>
      </template>

      <v-card>
        <v-list subheader two-line flat>
          <div class="d-flex align-center justify-space-between px-4 pt-3 pb-1">
            <span class="body-2 text--secondary">
              Add or remove your selection from clusters
              <span v-if="selectionCount > 1" class="d-block caption mt-1">
                {{ selectionCount }} selected — each cluster shows which are already members
              </span>
            </span>
            <v-btn small outlined color="primary" @click="createClusterDialog = true">
              <v-icon left small>mdi-plus</v-icon>
              New cluster
            </v-btn>
          </div>

          <v-card v-if="loading" flat>
            <v-overlay :value="loading" absolute :opacity="opacity">
              <v-progress-circular indeterminate size="36" color="primary"></v-progress-circular>
            </v-overlay>
          </v-card>
          <v-card v-else flat class="px-2 py-2 mx-2 my-2" height="450"
            style="flex-grow: 1 !important; overflow:auto !important;">
            <v-text-field v-model="clusterSearchQuery" dense outlined hide-details clearable
              prepend-inner-icon="mdi-magnify" label="Search clusters" placeholder="Filter by name..."
              class="mb-3"></v-text-field>

            <v-list class="cluster-select-list pa-0">
              <v-list-item v-if="filteredClusters.length === 0" class="text-center py-6 text--secondary">
                <v-list-item-content>
                  <v-list-item-subtitle>No clusters yet. Create one with "New cluster" above.</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-for="cluster in filteredClusters" :key="cluster.id" class="cluster-select-item">
                <v-list-item-content>
                  <v-list-item-title>{{ cluster.name || 'Unnamed cluster' }}</v-list-item-title>
                  <v-list-item-subtitle>
                    <span v-if="getClusterTypeName(cluster.cluster_type_id)" class="text--secondary">{{ getClusterTypeName(cluster.cluster_type_id) }} · </span>{{ (cluster.search_query?.[entityIDType] || []).length }} entities
                  </v-list-item-subtitle>
                  <v-list-item-subtitle
                    v-if="selectionCount > 0 && membershipSummary(cluster)"
                    :class="membershipSummaryClass(cluster)"
                    class="mt-1"
                  >
                    {{ membershipSummary(cluster) }}
                  </v-list-item-subtitle>
                  <div
                    v-if="selectionCount > 0 && showEntityBreakdown(cluster)"
                    class="cluster-membership-chips mt-1"
                  >
                    <v-chip
                      v-for="id in getMembership(cluster).inCluster"
                      :key="'in-' + cluster.id + '-' + id"
                      x-small
                      outlined
                      color="success"
                      class="mr-1 mb-1"
                    >
                      <v-icon left x-small>mdi-check</v-icon>
                      {{ entityLabel(id) }}
                    </v-chip>
                    <v-chip
                      v-for="id in getMembership(cluster).notInCluster"
                      :key="'out-' + cluster.id + '-' + id"
                      x-small
                      outlined
                      class="mr-1 mb-1 cluster-chip--new"
                    >
                      <v-icon left x-small>mdi-plus</v-icon>
                      {{ entityLabel(id) }}
                    </v-chip>
                  </div>
                  <div v-if="selectionCount > 0" class="cluster-row-actions mt-2">
                    <v-btn
                      v-if="canAddToCluster(cluster)"
                      small
                      depressed
                      color="primary"
                      class="mr-2 mb-1"
                      :loading="isClusterActionLoading(cluster.id, 'add')"
                      :disabled="!!clusterActionLoading"
                      @click.stop="addSelectionToCluster(cluster)"
                    >
                      <v-icon left small>mdi-plus</v-icon>
                      Add selection to cluster
                    </v-btn>
                    <v-btn
                      v-if="canRemoveFromCluster(cluster)"
                      small
                      outlined
                      color="error"
                      class="mb-1"
                      :loading="isClusterActionLoading(cluster.id, 'remove')"
                      :disabled="!!clusterActionLoading"
                      @click.stop="removeSelectionFromCluster(cluster)"
                    >
                      <v-icon left small>mdi-minus</v-icon>
                      Remove selection from cluster
                    </v-btn>
                  </div>
                </v-list-item-content>

                <v-list-item-action class="cluster-view-action">
                  <v-btn icon small :to="`/cluster/${cluster.id}`" @click.stop title="View cluster">
                    <v-icon small>mdi-arrow-right</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card>

          <v-dialog v-model="createClusterDialog" max-width="400" persistent>
            <v-card>
              <v-snackbar v-model="duplicateErrorSnackbar" color="error">
                A cluster with this name already exists.
                <template v-slot:action="{ attrs }">
                  <v-btn text v-bind="attrs" @click="duplicateErrorSnackbar = false">Close</v-btn>
                </template>
              </v-snackbar>
              <v-card-title>Create new cluster</v-card-title>
              <v-card-text>
                <v-text-field v-model="newClusterName" dense outlined hide-details label="Cluster name"
                  placeholder="Enter cluster name..." autofocus @keyup.enter="postCluster()"></v-text-field>
                <v-select v-model="newClusterTypeId" dense outlined hide-details label="Cluster type"
                  :items="clusterTypesForEntity" item-text="name" item-value="id" clearable
                  placeholder="Optional — select a type" class="mt-3"></v-select>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="closeCreateDialog">Cancel</v-btn>
                <v-btn color="primary" depressed :disabled="!newClusterName.trim()" :loading="creating"
                  @click="postCluster()">
                  Create
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

        </v-list>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog">
            Done
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import { getClusters, updateClusters, addCluster, deleteClusterEntities } from "../components/APIManager.js"
import { bus } from "../main.js";

// Hardcoded cluster types. entityTypeFilter: 'supplier' = only when creating a supplier cluster; null = both.
const CLUSTER_TYPES = [
  { id: 3, name: "Competitors", entityTypeFilter: null },
  { id: 2, name: "Customers", entityTypeFilter: null },
  { id: 1, name: "Partners", entityTypeFilter: null },
  { id: 6, name: "Target Accounts", entityTypeFilter: null },
  { id: 5, name: "Suppliers", entityTypeFilter: "supplier" },
  { id: 4, name: "Other", entityTypeFilter: null }
];

export default {
  props: {
    current_buyer_flag: Boolean,
    disabled: Boolean,
    entityType: String,
    entityIDType: String,
    entities: [],
    /** Optional map of entity id → display name for per-item membership chips */
    entityLabels: {
      type: Object,
      default: () => ({})
    },
    large: {
      default: true,
      type: Boolean
    }

  },
  data: () => ({
    newClusterName: "",
    newClusterTypeId: null,
    clusterSearchQuery: "",
    clusters: [],
    loading: true,
    clusterEntities: [],
    clusterActionLoading: null,
    creating: false,
    duplicateErrorSnackbar: false,
    fav: true,
    menu: false,
    createClusterDialog: false,
    message: false,
    hints: true,
    active: true,
    years: [2016, 2025],
    year_min: 2016,
    year_max: 2025,
    keyword_chips: [],
    keyword_items: [],
    selected_types: [],
    include_publisher: true,
    expiryRangeMenu: false,
    expiryRange: []
  }),

  mounted() {
    //this.loadClusters();
  },

  watch: {
    entityIDType(val, prev) {
      this.clusters = [];
      //this.loadClusters();
    }
  },

  computed: {
    clusterTypesForEntity() {
      const entity = (this.entityType || "").toLowerCase();
      return CLUSTER_TYPES.filter(
        ct => !ct.entityTypeFilter || ct.entityTypeFilter.toLowerCase() === entity
      );
    },
    isDuplicateClusterName() {
      const name = this.newClusterName.trim();
      if (!name) return false;
      const lower = name.toLowerCase();
      return this.clusters.some(c => c.name && c.name.toLowerCase() === lower);
    },
    filteredClusters() {
      if (!Array.isArray(this.clusters)) return [];
      if (!this.clusterSearchQuery.trim()) return this.clusters;
      const query = this.clusterSearchQuery.trim().toLowerCase();
      return this.clusters.filter(cluster =>
        (cluster.name || '').toLowerCase().includes(query)
      );
    },
    selectionCount() {
      return Array.isArray(this.entities) ? this.entities.length : 0;
    },
    hasEntityLabels() {
      return this.entityLabels && Object.keys(this.entityLabels).length > 0;
    }
  },

  methods: {
    async postCluster() {
      if (!this.newClusterName.trim()) return;
      if (this.isDuplicateClusterName) {
        this.duplicateErrorSnackbar = true;
        return;
      }

      this.creating = true;
      try {
        const results = await addCluster(encodeURIComponent(this.newClusterName), this.entityType, this.newClusterTypeId || null);
        this.parseClustersResponse(results);
        this.closeCreateDialog();
      } finally {
        this.creating = false;
      }
    },

    closeCreateDialog() {
      this.createClusterDialog = false;
      this.newClusterName = "";
      this.newClusterTypeId = null;
    },

    parseClustersResponse(results) {
      const raw = results?.data ?? results;
      this.clusters = Array.isArray(raw) ? raw : [];
      for (const cluster of this.clusters) {
        const sq = cluster.search_query;
        cluster.search_query = typeof sq === 'string' ? JSON.parse(sq) : (sq || {});
      }
    },

    closeDialog() {
      this.menu = false;
      this.$emit('clearSelections');
    },

    isClusterActionLoading(clusterId, action) {
      return this.clusterActionLoading?.clusterId === clusterId
        && this.clusterActionLoading?.action === action;
    },

    canAddToCluster(cluster) {
      return this.getMembership(cluster).notInCluster.length > 0;
    },

    canRemoveFromCluster(cluster) {
      return this.getMembership(cluster).inCluster.length > 0;
    },

    async refreshClusters() {
      const results = await getClusters(this.entityType);
      this.parseClustersResponse(results);
    },

    async addSelectionToCluster(cluster) {
      const { notInCluster } = this.getMembership(cluster);
      if (!notInCluster.length) return;

      this.clusterActionLoading = { clusterId: cluster.id, action: 'add' };
      try {
        const results = await updateClusters([cluster.id], this.entityType, notInCluster);
        this.parseClustersResponse(results);
      } finally {
        this.clusterActionLoading = null;
      }
    },

    async removeSelectionFromCluster(cluster) {
      const { inCluster } = this.getMembership(cluster);
      if (!inCluster.length) return;

      this.clusterActionLoading = { clusterId: cluster.id, action: 'remove' };
      try {
        await deleteClusterEntities(cluster.id, inCluster);
        await this.refreshClusters();
      } finally {
        this.clusterActionLoading = null;
      }
    },

    async loadClusters() {
      this.clusters = [];
      this.loading = true;
      this.clusterActionLoading = null;

      try {
        await this.refreshClusters();
      } catch (e) {
        this.clusters = [];
      } finally {
        this.loading = false;
      }
    },

    getClusterTypeName(clusterTypeId) {
      if (clusterTypeId == null || clusterTypeId === undefined) return "";
      const ct = CLUSTER_TYPES.find(t => t.id === clusterTypeId);
      return ct ? ct.name : "";
    },

    normalizeEntityId(id) {
      return id == null ? '' : String(id);
    },

    entityInCluster(clusterEntities, entityId) {
      if (!Array.isArray(clusterEntities)) return false;
      const target = this.normalizeEntityId(entityId);
      return clusterEntities.some(item => this.normalizeEntityId(item) === target);
    },

    getMembership(cluster) {
      const clusterEntities = cluster?.search_query?.[this.entityIDType];
      const clusterArr = Array.isArray(clusterEntities) ? clusterEntities : [];
      const selected = Array.isArray(this.entities) ? this.entities : [];
      const inCluster = [];
      const notInCluster = [];

      for (const id of selected) {
        if (this.entityInCluster(clusterArr, id)) {
          inCluster.push(id);
        } else {
          notInCluster.push(id);
        }
      }

      return {
        inCluster,
        notInCluster,
        total: selected.length,
        allIn: selected.length > 0 && inCluster.length === selected.length,
        noneIn: inCluster.length === 0,
        partial: inCluster.length > 0 && inCluster.length < selected.length
      };
    },

    membershipSummary(cluster) {
      const { inCluster, notInCluster, total, allIn, noneIn } = this.getMembership(cluster);
      if (total === 0) return '';
      if (total === 1) {
        return allIn ? 'Already in cluster' : noneIn ? 'Not in cluster' : '';
      }
      if (allIn) return `All ${total} selected already in cluster`;
      if (noneIn) return `None in cluster — ${total} will be added`;
      return `${inCluster.length} of ${total} already in cluster · ${notInCluster.length} to add`;
    },

    membershipSummaryClass(cluster) {
      const { allIn, noneIn, partial } = this.getMembership(cluster);
      if (allIn) return 'success--text';
      if (noneIn) return 'text--secondary';
      if (partial) return 'warning--text text--darken-2';
      return '';
    },

    showEntityBreakdown(cluster) {
      if (this.selectionCount === 0) return false;
      if (this.selectionCount === 1) return this.hasEntityLabels;
      return this.hasEntityLabels && this.selectionCount <= 12;
    },

    entityLabel(id) {
      const key = this.normalizeEntityId(id);
      const labels = this.entityLabels || {};
      if (labels[id] != null) return labels[id];
      if (labels[key] != null) return labels[key];
      return `#${id}`;
    },

    year() {
      var d = new Date();
      return d.getFullYear();
    },

    remove_kw(item) {
      this.keyword_chips.splice(this.keyword_chips.indexOf(item), 1);
      this.keyword_chips = [...this.keyword_chips];
    },

    clear_expiryRange() {
      this.expiryRange = [];
    },

    update_search() {
      let additional_filters = {
        keywords: this.keyword_chips,
        expiry_from: this.expiryRange[0],
        expiry_to: this.expiryRange[1],
        type: this.selected_types,
        include_publisher: this.include_publisher
      };

      bus.$emit("update-filter", additional_filters);
    }
  }
};
</script>

<style scoped>
.cluster-select-dialog {
  display: flex;
  align-items: center;
  justify-content: center;
}

.cluster-select-list ::v-deep .v-list-item:hover {
  background: rgba(var(--v-primary-base), 0.08) !important;
}

.cluster-select-item ::v-deep .v-list-item__content {
  padding-top: 8px;
  padding-bottom: 8px;
}

.cluster-row-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.cluster-view-action {
  align-self: flex-start;
  margin-top: 4px;
}

.cluster-membership-chips {
  line-height: 1.4;
}

.cluster-chip--new {
  opacity: 0.85;
}
</style>
